/**
 * Build absolute upload progress WebSocket URL.
 * VUE_APP_SOCKET_URL is like "ws://host/ws"; response websocket_url is like "ws/uploads/<id>/".
 */
export function buildUploadWebSocketUrl(websocketUrl) {
  const socketBase = (process.env.VUE_APP_SOCKET_URL || '').replace(/\/$/, '');
  let path = String(websocketUrl || '').replace(/^\//, '');

  if (path.startsWith('ws/')) {
    path = path.slice(3);
  }

  return `${socketBase}/${path}`;
}

const TERMINAL_UPLOAD_STATUSES = new Set(['completed', 'failed']);
const TERMINAL_BATCH_STATUSES = new Set(['completed', 'failed', 'partially_failed']);
const BATCH_EVENT_TYPES = new Set(['batch_upload_progress', 'batch_file_progress']);

function createProgressWatcher({
  websocketUrl,
  onProgress,
  onTerminal,
  onConnectionError,
  isTerminalEvent,
  isAcceptedMessage,
  maxReconnectAttempts = 5,
  logLabel = 'upload progress'
} = {}) {
  let ws = null;
  let closedIntentionally = false;
  let destroyed = false;
  let reconnectAttempt = 0;
  let reconnectTimer = null;

  const cleanupTimers = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const closeSocket = () => {
    if (!ws) return;
    const socket = ws;
    ws = null;
    try {
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        socket.close();
      }
    } catch (error) {
      console.error(`Error closing ${logLabel} WebSocket:`, error);
    }
  };

  const finish = (data) => {
    closedIntentionally = true;
    cleanupTimers();
    closeSocket();
    onTerminal?.(data);
  };

  const failConnection = (message) => {
    closedIntentionally = true;
    cleanupTimers();
    closeSocket();
    onConnectionError?.({
      message,
      error: message,
      status: 'failed'
    });
  };

  const handleProgressPayload = (data) => {
    if (!data) return;

    onProgress?.(data);

    if (isTerminalEvent?.(data)) {
      finish(data);
    }
  };

  const connect = () => {
    if (destroyed || closedIntentionally) return;

    if (!websocketUrl) {
      failConnection(`${logLabel} WebSocket URL is missing`);
      return;
    }

    const url = buildUploadWebSocketUrl(websocketUrl);
    console.log(`Connecting to ${logLabel} WebSocket:`, url);

    try {
      ws = new WebSocket(url);
    } catch (error) {
      console.error(`Failed to open ${logLabel} WebSocket:`, error);
      failConnection(error?.message || `Failed to open ${logLabel} WebSocket`);
      return;
    }

    ws.onopen = () => {
      reconnectAttempt = 0;
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (isAcceptedMessage && !isAcceptedMessage(data)) return;
        handleProgressPayload(data);
      } catch (error) {
        console.error(`Error parsing ${logLabel} WebSocket message:`, error);
      }
    };

    ws.onerror = () => {
      // Reconnect is handled in onclose
    };

    ws.onclose = () => {
      ws = null;
      if (closedIntentionally || destroyed) return;

      if (reconnectAttempt < maxReconnectAttempts) {
        const delay = Math.min(1000 * (2 ** reconnectAttempt), 15000);
        reconnectAttempt += 1;
        console.log(`${logLabel} WebSocket reconnecting (attempt ${reconnectAttempt}/${maxReconnectAttempts}) in ${delay}ms`);
        reconnectTimer = setTimeout(connect, delay);
        return;
      }

      failConnection(`${logLabel} WebSocket connection lost. Please try again.`);
    };
  };

  connect();

  return () => {
    destroyed = true;
    closedIntentionally = true;
    cleanupTimers();
    closeSocket();
  };
}

/**
 * Watch upload extraction progress over WebSocket only
 * (progress-only; file was already uploaded via HTTP).
 *
 * @returns {Function} cleanup / close
 */
export function watchUploadProgress({
  websocketUrl,
  uploadId,
  onProgress,
  onDone,
  onError,
  maxReconnectAttempts = 5
} = {}) {
  const resolvedWebsocketUrl = websocketUrl
    || (uploadId ? `ws/uploads/${uploadId}/` : null);

  return createProgressWatcher({
    websocketUrl: resolvedWebsocketUrl,
    onProgress,
    isAcceptedMessage: (data) => !data.type || data.type === 'upload_progress',
    isTerminalEvent: (data) => TERMINAL_UPLOAD_STATUSES.has(data.status),
    maxReconnectAttempts,
    logLabel: 'upload progress',
    onTerminal: (data) => {
      if (data?.status === 'completed') {
        onDone?.(data);
        return;
      }
      onError?.(data || { message: 'Upload failed', status: 'failed' });
    },
    onConnectionError: (error) => {
      onError?.(error);
    }
  });
}

/**
 * Watch batch upload progress over a single WebSocket.
 * Handles both overall batch events and per-file events on the same socket.
 *
 * @returns {Function} cleanup / close
 */
export function watchBatchUploadProgress({
  websocketUrl,
  batchId,
  onBatchProgress,
  onFileProgress,
  onDone,
  onPartial,
  onError,
  maxReconnectAttempts = 5
} = {}) {
  const resolvedWebsocketUrl = websocketUrl
    || (batchId ? `ws/batch-uploads/${batchId}/` : null);

  return createProgressWatcher({
    websocketUrl: resolvedWebsocketUrl,
    isAcceptedMessage: (data) => !data.type || BATCH_EVENT_TYPES.has(data.type),
    isTerminalEvent: (data) => (
      data.type === 'batch_upload_progress'
      && TERMINAL_BATCH_STATUSES.has(data.status)
    ),
    maxReconnectAttempts,
    logLabel: 'batch upload progress',
    onProgress: (data) => {
      if (data.type === 'batch_file_progress') {
        onFileProgress?.(data);
        return;
      }

      // batch_upload_progress (or untyped batch payloads)
      onBatchProgress?.(data);
    },
    onTerminal: (data) => {
      if (data?.status === 'completed') {
        onDone?.(data);
        return;
      }
      if (data?.status === 'partially_failed') {
        onPartial?.(data);
        return;
      }
      onError?.(data || { message: 'Batch upload failed', status: 'failed' });
    },
    onConnectionError: (error) => {
      onError?.(error);
    }
  });
}

export default watchUploadProgress;
